import React from 'react';
import {
  Clock,
  Calendar,
  DollarSign,
  FileText,
  ExternalLink,
  CheckCircle,
  Users,
  ArrowRight,
} from 'lucide-react';
import SectionHeader from '../ui/SectionHeader';

const HRPage = ({ onNavigate }) => {
  return (
    <div className="animate-stagger">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-text-primary mb-2">Recursos Humanos</h1>
        <p className="text-text-secondary">
          Gestión de personal, horarios, vacaciones y nóminas
        </p>
      </div>

      <section className="section">
        <button
          type="button"
          onClick={() => onNavigate('/recursos-humanos/disponibilidad')}
          className="card w-full p-6 text-left border border-primary-lighter hover:border-primary transition-colors group"
        >
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="w-14 h-14 rounded-xl bg-primary-lighter flex items-center justify-center flex-shrink-0">
              <Calendar size={28} className="text-primary" />
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-semibold text-text-primary group-hover:text-primary transition-colors">
                Disponibilidad del equipo
              </h2>
              <p className="text-text-secondary mt-1">
                Consulta los días y horarios de trabajo de todos los empleados.
              </p>
            </div>
            <ArrowRight className="text-primary flex-shrink-0" />
          </div>
        </button>
      </section>

      {/* PayFit Main Card */}
      <section id="payfit" className="section scroll-mt-24">
        <div className="card overflow-hidden">
          <div className="bg-gradient-to-r from-indigo-500 to-purple-600 p-8 text-white">
            <div className="flex items-center gap-6">
              <div className="w-20 h-20 bg-white rounded-2xl flex items-center justify-center">
                <Users className="w-12 h-12 text-indigo-600" />
              </div>
              <div>
                <h2 className="text-3xl font-bold mb-2">PayFit</h2>
                <p className="text-white/80 text-lg">Gestión de Recursos Humanos</p>
              </div>
            </div>
          </div>
          
          <div className="p-8">
            <p className="text-text-secondary text-lg mb-8 leading-relaxed">
              Gestiona tu horario, vacaciones, nóminas y más desde una única plataforma. 
              PayFit centraliza toda la información relacionada con tu empleo en Sonneil.
            </p>

            {/* Features Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="bg-background rounded-xl p-5 text-center">
                <div className="w-12 h-12 bg-primary-lighter rounded-xl flex items-center justify-center mx-auto mb-3">
                  <Clock size={24} className="text-primary" />
                </div>
                <h4 className="font-semibold text-text-primary mb-1">Control Horario</h4>
                <p className="text-sm text-text-tertiary">Registro de entrada y salida</p>
              </div>

              <div className="bg-background rounded-xl p-5 text-center">
                <div className="w-12 h-12 bg-success/10 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <Calendar size={24} className="text-success" />
                </div>
                <h4 className="font-semibold text-text-primary mb-1">Vacaciones</h4>
                <p className="text-sm text-text-tertiary">Solicitud y gestión de días</p>
              </div>

              <div className="bg-background rounded-xl p-5 text-center">
                <div className="w-12 h-12 bg-info/10 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <DollarSign size={24} className="text-info" />
                </div>
                <h4 className="font-semibold text-text-primary mb-1">Nóminas</h4>
                <p className="text-sm text-text-tertiary">Consulta tu nómina mensual</p>
              </div>

              <div className="bg-background rounded-xl p-5 text-center">
                <div className="w-12 h-12 bg-warning/10 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <FileText size={24} className="text-warning" />
                </div>
                <h4 className="font-semibold text-text-primary mb-1">Documentos</h4>
                <p className="text-sm text-text-tertiary">Contratos y certificados</p>
              </div>
            </div>

            {/* CTA Button */}
            <div className="flex justify-center">
              <a
                href="https://app.payfit.com"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary btn-lg"
              >
                Acceder a PayFit
                <ExternalLink size={20} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Actions */}
      <section className="section">
        <SectionHeader
          title="Acciones Rápidas"
          description="Accede directamente a las funciones más utilizadas"
        />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <a
            id="fichar"
            href="https://app.payfit.com/ticking"
            target="_blank"
            rel="noopener noreferrer"
            className="card p-5 flex items-center gap-4 hover:border-primary/30 border border-transparent transition-all group scroll-mt-24"
          >
            <div className="w-12 h-12 rounded-xl bg-primary-lighter/50 flex items-center justify-center">
              <Clock size={24} className="text-primary" />
            </div>
            <div>
              <h4 className="font-medium text-text-primary group-hover:text-primary transition-colors">
                Fichar entrada/salida
              </h4>
              <p className="text-sm text-text-tertiary">Registro diario</p>
            </div>
          </a>

          <a
            id="vacaciones"
            href="https://app.payfit.com/employee/623d90f99dab73d7e38e6eee/absences/new-xp/ES/623d90f99dab73d7e38e6eee/balance"
            target="_blank"
            rel="noopener noreferrer"
            className="card p-5 flex items-center gap-4 hover:border-primary/30 border border-transparent transition-all group scroll-mt-24"
          >
            <div className="w-12 h-12 rounded-xl bg-success/10 flex items-center justify-center">
              <Calendar size={24} className="text-success" />
            </div>
            <div>
              <h4 className="font-medium text-text-primary group-hover:text-primary transition-colors">
                Solicitar vacaciones
              </h4>
              <p className="text-sm text-text-tertiary">Nueva solicitud</p>
            </div>
          </a>

          <a
            id="nominas"
            href="https://app.payfit.com/payslips"
            target="_blank"
            rel="noopener noreferrer"
            className="card p-5 flex items-center gap-4 hover:border-primary/30 border border-transparent transition-all group scroll-mt-24"
          >
            <div className="w-12 h-12 rounded-xl bg-info/10 flex items-center justify-center">
              <DollarSign size={24} className="text-info" />
            </div>
            <div>
              <h4 className="font-medium text-text-primary group-hover:text-primary transition-colors">
                Ver nóminas
              </h4>
              <p className="text-sm text-text-tertiary">Histórico de pagos</p>
            </div>
          </a>
        </div>
      </section>

      {/* Info Card */}
      <section className="mt-8 p-6 bg-primary-lighter/20 rounded-2xl border border-primary-lighter">
        <div className="flex items-start gap-4">
          <CheckCircle size={24} className="text-primary flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-semibold text-text-primary mb-2">¿Primera vez en PayFit?</h3>
            <p className="text-text-secondary text-sm">
              Si es tu primer acceso, utiliza el email corporativo de Sonneil para iniciar sesión. 
              Recibirás un enlace de activación en tu correo. Si tienes problemas, contacta con{' '}
              <a href="mailto:rrhh@sonneil.com" className="text-primary hover:underline">
                rrhh@sonneil.com
              </a>
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HRPage;

